import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '../utils/api';
import { Package, Plus, Search, Trash2, Edit2, AlertCircle, CheckCircle2, TrendingUp, Filter } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const InventoryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Create / Edit modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('kg');
  const [currentStock, setCurrentStock] = useState('');
  const [minStockAlert, setMinStockAlert] = useState('');
  const [costPerUnit, setCostPerUnit] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/inventory');
      setItems(response.data);
    } catch (error) {
      toast.error('Stok bilgileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditItemId(null);
    setName('');
    setUnit('kg');
    setCurrentStock('');
    setMinStockAlert('');
    setCostPerUnit('');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditItemId(item.id);
    setName(item.name);
    setUnit(item.unit);
    setCurrentStock(item.current_stock);
    setMinStockAlert(item.min_stock_alert);
    setCostPerUnit(item.cost_per_unit || 0);
    setIsModalOpen(true);
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    if (!name || currentStock === '' || minStockAlert === '') {
      toast.error('Lütfen tüm zorunlu alanları doldurun.');
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        name,
        unit,
        current_stock: parseFloat(currentStock),
        min_stock_alert: parseFloat(minStockAlert),
        cost_per_unit: costPerUnit ? parseFloat(costPerUnit) : 0.0
      };

      if (editItemId) {
        await apiClient.put(`/inventory/${editItemId}`, payload);
        toast.success('Stok kalemi başarıyla güncellendi.');
      } else {
        await apiClient.post('/inventory', payload);
        toast.success('Yeni stok kalemi başarıyla eklendi.');
      }

      setIsModalOpen(false);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kaydedilirken hata oluştu.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Bu stok kalemini silmek istediğinize emin misiniz?')) return;
    try {
      await apiClient.delete(`/inventory/${id}`);
      toast.success('Stok kalemi silindi.');
      fetchInventory();
    } catch (error) {
      toast.error('Silinirken hata oluştu.');
    }
  };

  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const lowStockItems = items.filter(item => item.current_stock <= item.min_stock_alert);

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Package className="text-orange-500" size={32} />
              Stok ve Depo Yönetimi
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Hammaddelerinizi takip edin, kritik stok uyarılarını yönetin ve ürün reçetesi altyapısını kurun
            </p>
          </div>
          <Button onClick={handleOpenAddModal} className="bg-orange-600 hover:bg-orange-700 text-white font-bold h-11 px-6 shadow-lg shadow-orange-600/20 self-start md:self-auto">
            <Plus size={20} className="mr-2" /> Stok Kalemi Ekle
          </Button>
        </div>

        {/* Stok Metrikleri */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-6 flex items-center justify-between shadow-sm">
            <div>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Toplam Stok Kalemi</p>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-1 font-mono">{items.length}</h2>
            </div>
            <div className="p-3 bg-blue-500/10 rounded-2xl border border-blue-500/20 text-blue-500">
              <Package size={24} />
            </div>
          </Card>

          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-6 flex items-center justify-between shadow-sm">
            <div>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Kritik Stok Uyarısı</p>
              <h2 className="text-2xl font-bold text-red-500 mt-1 font-mono">{lowStockItems.length}</h2>
            </div>
            <div className="p-3 bg-red-500/10 rounded-2xl border border-red-500/20 text-red-500">
              <AlertCircle size={24} />
            </div>
          </Card>

          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-6 flex items-center justify-between shadow-sm">
            <div>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Tahmini Depo Değeri</p>
              <h2 className="text-2xl font-bold text-green-500 mt-1 font-mono">
                {items.reduce((sum, i) => sum + (i.current_stock * (i.cost_per_unit || 0)), 0).toFixed(2)} ₺
              </h2>
            </div>
            <div className="p-3 bg-green-500/10 rounded-2xl border border-green-500/20 text-green-500">
              <TrendingUp size={24} />
            </div>
          </Card>
        </div>

        {/* Filtreleme ve Tablo */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                type="text"
                placeholder="Hammadde ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
              />
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="py-12 text-center text-slate-500">Stoklar yükleniyor...</div>
            ) : filteredItems.length === 0 ? (
              <div className="py-12 text-center text-slate-500">Stok kalemi bulunamadı.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                      <th className="pb-3 font-semibold">Hammadde Adı</th>
                      <th className="pb-3 font-semibold">Mevcut Stok</th>
                      <th className="pb-3 font-semibold">Birim</th>
                      <th className="pb-3 font-semibold">Kritik Alarm Seviyesi</th>
                      <th className="pb-3 font-semibold">Birim Maliyet</th>
                      <th className="pb-3 font-semibold">Durum</th>
                      <th className="pb-3 font-semibold text-right">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800 text-slate-700 dark:text-slate-300 text-sm">
                    {filteredItems.map(item => {
                      const isLowStock = item.current_stock <= item.min_stock_alert;
                      return (
                        <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                          <td className="py-4 font-bold text-slate-900 dark:text-white">{item.name}</td>
                          <td className="py-4 font-mono font-bold text-base">{item.current_stock}</td>
                          <td className="py-4 text-slate-500">{item.unit}</td>
                          <td className="py-4 font-mono text-slate-500">{item.min_stock_alert} {item.unit}</td>
                          <td className="py-4 font-mono">{(item.cost_per_unit || 0).toFixed(2)} ₺</td>
                          <td className="py-4">
                            {isLowStock ? (
                              <Badge className="bg-red-500 text-white font-bold gap-1 flex items-center w-max px-2.5 py-1">
                                <AlertCircle size={14} /> Kritik Stok
                              </Badge>
                            ) : (
                              <Badge className="bg-green-500 text-white font-bold gap-1 flex items-center w-max px-2.5 py-1">
                                <CheckCircle2 size={14} /> Yeterli
                              </Badge>
                            )}
                          </td>
                          <td className="py-4 text-right space-x-2">
                            <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(item)} className="text-slate-600 dark:text-slate-400 hover:text-orange-500 hover:bg-orange-500/10">
                              <Edit2 size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(item.id)} className="text-slate-600 dark:text-slate-400 hover:text-red-500 hover:bg-red-500/10">
                              <Trash2 size={16} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Modal / Dialog */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Package className="text-orange-500" size={22} />
              {editItemId ? 'Stok Kalemini Düzenle' : 'Yeni Stok Kalemi Ekle'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveItem} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300 font-bold">Hammadde Adı</Label>
              <Input
                placeholder="Örn: 150g Dana Burger Köftesi, Dilim Peynir"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300 font-bold">Ölçü Birimi</Label>
                <select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white text-sm focus:outline-none"
                >
                  <option value="kg">Kilogram (kg)</option>
                  <option value="gr">Gram (gr)</option>
                  <option value="adet">Adet</option>
                  <option value="litre">Litre (L)</option>
                  <option value="ml">Mililitre (ml)</option>
                  <option value="paket">Paket</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300 font-bold">Mevcut Stok Miktarı</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Örn: 50"
                  value={currentStock}
                  onChange={(e) => setCurrentStock(e.target.value)}
                  className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white font-mono"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300 font-bold">Kritik Stok Alarm Limiti</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Örn: 10"
                  value={minStockAlert}
                  onChange={(e) => setMinStockAlert(e.target.value)}
                  className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white font-mono"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300 font-bold">Birim Maliyet (₺)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Örn: 120.50"
                  value={costPerUnit}
                  onChange={(e) => setCostPerUnit(e.target.value)}
                  className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white font-mono"
                />
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="ghost" onClick={() => setIsModalOpen(false)} className="text-slate-600 dark:text-slate-400">
                İptal
              </Button>
              <Button type="submit" disabled={isSaving} className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-6">
                {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default InventoryPage;
